import { Button, Divider, Form, FormListFieldData, Tooltip } from "antd";
import { SmartSearchMemo } from "@src/pages/intelligence-search-v2/components/json-query/search-component/smart-search-template";
import React from "react";
import { SmlAsset, SmlJsonQuery, SmlJsonQueryFilter, SmlJsonQueryOrder } from "@src/types/intelligence-search";
import { FormInstance } from "antd/lib/form";

import { useChartWrapperContext } from "@src/contexts/ab-testing/chart-wrapper-context";
import { CloseCircleOutlined, DownOutlined, SyncOutlined, UpOutlined } from "@ant-design/icons";
import { orange } from "@ant-design/colors";
import { css } from "@emotion/react";
import { NarrativeTitle } from "@src/pages/intelligence-search-v2/components/json-query/result/auto-generated-title";
import { IJobMeta } from "@src/types/ab-testing-exploration";
import { useSmlQueryContext } from "@src/pages/intelligence-search-v2/context/sml-query-context";
import { SearchResultCard } from "@src/pages/intelligence-search-v2/components/json-query/result/search-result-card";
import { useSmlQueryBuilderContext } from "@src/pages/intelligence-search-v2/context/sml-query-builder-context";
import { DEFAULT_LIMIT, filterOutOrderWithoutFilter, OrderAndLimitConfig } from "../order-limit";
import { JsonQueryBuilderConfig } from "../query-builder";
import { config } from "@src/config";
import { useQueryDependenciesGraphContext } from "@src/pages/intelligence-search-v2/context/questions-context";
import { LoadResponse } from "@src/pages/intelligence-search-v2/hooks/use-load";
import { MoreActionQuery } from "@src/pages/intelligence-search/component/more-action";
import dayjs from "dayjs";
import {
    QueryStatus,
    renderListErrorMessage,
} from "@src/pages/intelligence-search/component/search-component/query-status";
import { IntelligenceFormFieldsEnum } from "@src/pages/intelligence-search-v2/enum";
import { convertFilterToPayload } from "@src/hooks/intelligence-search/use-fetch-next-options";
import { mergeRequiredFilter } from "@src/util/intelligence-search/merge-required-filter-to-payload";
import { PERMISSION_EXCLUDED_CHECK, PERMISSION_LAYOUT_CHECK } from "@src/components/crud-permission-layout";
import { AnimatedWrapper } from "@src/components/animation-wrapper";

export type SearchResultType = {
    data: Record<string, any>[];
    query: string;
    meta: IJobMeta;
    cache_hit: boolean;
    schema: any[];
    ref_assets: SmlAsset[];
};

export type DryRunInfo = {
    loading: boolean;
    message?: string[];
    statusCode?: number;
    meta?: Record<string, any>;
};
export const getValueWithDefault = (value: JsonQueryBuilderConfig | undefined) => {
    return {
        limit: value?.limit ?? DEFAULT_LIMIT,
        orders: value?.orders ?? [],
        selected_assets_or_filters: value?.selected_assets_or_filters ?? [],
    };
};
const SearchComponent: React.FC<{
    queryKey: string;
    form: FormInstance;
    value?: JsonQueryBuilderConfig;
    onChange?: (jsonQuery: SmlJsonQuery, builderConfig: any) => void;
    formListFieldData: FormListFieldData;
    productCode?: string;
    platform?: string[];
    timeRange: dayjs.Dayjs[];
}> = ({ form, queryKey, onChange, formListFieldData, value, productCode, platform, timeRange }) => {
    const previousQuery = React.useRef<string>();
    const previousQueryLimit = React.useRef<number>();

    const { limit, orders, selected_assets_or_filters } = getValueWithDefault(value);

    const selectedAssetsOrFilters = React.useMemo(() => {
        return (selected_assets_or_filters ?? []) as SmlJsonQueryFilter[];
    }, [JSON.stringify(selected_assets_or_filters)]);

    const [previousFilters, setPreviousFilters] = React.useState<SmlJsonQueryFilter[]>([]);

    const [isQueryComplete, setIsQueryComplete] = React.useState<boolean>(false);
    const [isNetworkError, setIsNetworkError] = React.useState<boolean>(false);
    const [isShowExploreQuestion, setIsShowExploreQuestion] = React.useState(false);

    const enoughRequireField =
        !!productCode && !!platform && platform?.length > 0 && !!timeRange && timeRange?.length > 0;
    const { metaDictionary } = useQueryDependenciesGraphContext();

    const { getGeneratedJsonQuery } = useSmlQueryBuilderContext();
    const {
        loadResult,
        referencedAssets,
        dryRunQueryHasError,
        isDoingDryRunQuery,
        isLoading,
        isLoadingData,
        loadData,
        dryRunQueryError,
        dryRunQuery,
        dryRunResult,
    } = useSmlQueryContext();

    const payload = React.useMemo(() => {
        return convertFilterToPayload(
            mergeRequiredFilter({
                platform: platform ?? [],
                timeRange: (timeRange ?? [])?.map((t: any) => t.format(config.DATE_FORMAT)),
                selectionFilters: selectedAssetsOrFilters,
            }),
            orders ?? [],
            limit,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAssetsOrFilters, JSON.stringify([platform, timeRange, orders, metaDictionary, limit])]);
    const generatedJsonQuery = getGeneratedJsonQuery(payload);
    const handleChange = ({
        selectedAssetsOrFilters,
        limit,
        orders,
    }: {
        selectedAssetsOrFilters?: SmlJsonQueryFilter[];
        orders?: SmlJsonQueryOrder[];
        limit?: number;
    }) => {
        const currentValue = form.getFieldValue([
            IntelligenceFormFieldsEnum.QUESTIONS,
            formListFieldData.name,
            IntelligenceFormFieldsEnum.JSON_QUERY_BUILDER_CONFIG,
        ]);

        const _selectedAssetsOrFilters = (selectedAssetsOrFilters ??
            currentValue?.selected_assets_or_filters ??
            []) as SmlJsonQueryFilter[];
        const _orders = filterOutOrderWithoutFilter(orders ?? currentValue?.orders ?? [], _selectedAssetsOrFilters);
        const _limit = limit ?? currentValue?.limit ?? DEFAULT_LIMIT;
        const _timeRange = (timeRange ?? [])?.map((t: any) => t.format(config.DATE_FORMAT));
        const _payload = convertFilterToPayload(
            mergeRequiredFilter({
                platform: platform ?? [],
                timeRange: _timeRange,
                selectionFilters: _selectedAssetsOrFilters,
            }),
            _orders,
            _limit,
        );
        const _jsonQueryBuilderConfig = {
            selected_assets_or_filters: _selectedAssetsOrFilters,
            orders: _orders,
            limit: _limit,
        };
        onChange?.(_payload, _jsonQueryBuilderConfig);
    };
    React.useEffect(() => {
        handleChange({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [platform, timeRange]);

    const realtimeQuery = JSON.stringify({
        productCode: productCode,
        payload: generatedJsonQuery,
    });

    const [isSetInitForPreviousQuery, setIsSetInitForPreviousQuery] = React.useState(false);
    const [isUserClickApplyFirstTime, setIsUserClickApplyFirstTime] = React.useState(false);

    const handleAfterLoadData = React.useCallback(() => {
        previousQuery.current = realtimeQuery;
        previousQueryLimit.current = limit;
        setPreviousFilters(selectedAssetsOrFilters);
    }, [realtimeQuery, limit, selectedAssetsOrFilters]);

    React.useLayoutEffect(() => {
        if (!generatedJsonQuery) return;
        if (isSetInitForPreviousQuery) return;
        const timeout = setTimeout(() => {
            handleAfterLoadData();
            setIsSetInitForPreviousQuery(true);
        }, 100);
        return () => clearTimeout(timeout);
    }, [handleAfterLoadData, isSetInitForPreviousQuery, generatedJsonQuery]);

    React.useEffect(() => {
        if (!generatedJsonQuery) return;
        setIsQueryComplete(false);
        dryRunQuery(1000);
    }, [JSON.stringify(generatedJsonQuery)]);

    const isQueryChange = previousQuery.current !== realtimeQuery || previousQueryLimit.current !== limit;
    const isDisabled =
        !isNetworkError && ((isUserClickApplyFirstTime && !isQueryChange) || dryRunQueryHasError || isLoading);
    const isShowWarning =
        !isNetworkError && isUserClickApplyFirstTime && isSetInitForPreviousQuery && isQueryChange && !isDisabled;

    if (!enoughRequireField) return null;

    const handleLoad = async () => {
        setIsUserClickApplyFirstTime(true);
        setIsQueryComplete(false);

        loadData(0)
            .then((data) => {
                setIsNetworkError(false);
                setIsQueryComplete(true);
                handleAfterLoadData();
            })
            .catch((err) => {
                if (err.code === "ERR_NETWORK") {
                    setIsNetworkError(true);
                }
            });
    };

    return (
        <div className={`relative ${PERMISSION_LAYOUT_CHECK}`}>
            <div className="absolute -top-12 right-0">
                <MoreActionQuery form={form} />
            </div>
            <UpdateChartState
                loadResult={loadResult}
                realtimeQuery={realtimeQuery}
                renderedQuery={dryRunResult?.query}
            />
            <div className=" bg-white z-50 mb-2 ">
                <div className="flex items-end bg-gray-100/50 border-[1px] border-solid border-gray-100 rounded-md">
                    <SmartSearchMemo
                        queryKey={queryKey}
                        productCode={productCode}
                        platform={platform}
                        timeRange={timeRange}
                        filters={selectedAssetsOrFilters}
                        handleChange={handleChange}
                    />
                </div>
            </div>
            <div className="mb-2 flex justify-between">
                <div className="flex items-start space-x-2 flex-1">
                    <div className="mt-1">
                        <QueryStatus
                            isUserChangeFirstTime={true}
                            isLoading={isLoadingData}
                            isQueryComplete={isQueryComplete}
                            dryRun={{
                                loading: isDoingDryRunQuery,
                                message: dryRunQueryError?.message ?? [],
                                statusCode: dryRunQueryError?.statusCode,
                                meta: dryRunResult?.meta,
                            }}
                        />
                    </div>
                    <Button
                        style={{
                            opacity: dryRunQueryError?.statusCode || isDoingDryRunQuery ? 0 : 1,
                        }}
                        type="link"
                        onClick={() => setIsShowExploreQuestion(!isShowExploreQuestion)}
                        className="font-normal -translate-y-0.5"
                    >
                        {isShowExploreQuestion ? "Hide explore question" : "Show explore question"}
                        {isShowExploreQuestion ? <UpOutlined /> : <DownOutlined />}
                    </Button>
                </div>
                <div className="flex items-center space-x-2 mb-2">
                    <OrderAndLimitConfig filters={selectedAssetsOrFilters} handleChange={handleChange} value={value} />
                    <div className="">
                        <Divider className="border-gray-400 mx-3" type="vertical" />
                    </div>

                    <Button
                        type="default"
                        onClick={() => {
                            handleChange({
                                selectedAssetsOrFilters: previousFilters,
                            });
                        }}
                        disabled={previousFilters.length === 0 || selectedAssetsOrFilters === previousFilters}
                    >
                        Reset
                    </Button>

                    <Button
                        // loading={isLoading}
                        type="default"
                        onClick={() => {
                            handleChange({
                                selectedAssetsOrFilters: [],
                            });
                        }}
                        disabled={isDoingDryRunQuery || isLoading || selectedAssetsOrFilters.length === 0}
                    >
                        Clear
                    </Button>

                    <Tooltip
                        title={
                            dryRunQueryHasError
                                ? renderListErrorMessage(dryRunQueryError?.message ?? [])
                                : isShowWarning
                                ? "The data outdated. Click to refresh."
                                : ""
                        }
                        destroyOnHidden
                    >
                        <Button
                            className={PERMISSION_EXCLUDED_CHECK}
                            css={
                                isShowWarning
                                    ? css`
                                          background-color: ${orange[5]};
                                          color: black;
                                          &:hover {
                                              background-color: ${orange[6]} !important;
                                          }
                                      `
                                    : css``
                            }
                            loading={isLoading}
                            type={dryRunQueryHasError ? "default" : "primary"}
                            danger={dryRunQueryHasError}
                            onClick={() => {
                                if (isDisabled) return;

                                form.validateFields(
                                    [
                                        [
                                            IntelligenceFormFieldsEnum.QUESTIONS,
                                            formListFieldData.name,
                                            IntelligenceFormFieldsEnum.QUERY_DEPENDENCIES,
                                        ],
                                    ],
                                    {
                                        recursive: true,
                                    },
                                )
                                    .then(() => {
                                        handleLoad();
                                    })
                                    .catch((err) => {
                                        console.log("err ", err);
                                    });
                            }}
                            disabled={isDisabled}
                            icon={
                                dryRunQueryHasError ? (
                                    <CloseCircleOutlined className={PERMISSION_EXCLUDED_CHECK} />
                                ) : (
                                    <SyncOutlined className={PERMISSION_EXCLUDED_CHECK} />
                                )
                            }
                        >
                            Apply
                        </Button>
                    </Tooltip>
                </div>
            </div>
            <AnimatedWrapper isVisible={isShowExploreQuestion}>
                <NarrativeTitle
                    disabled={dryRunQueryHasError}
                    loading={isDoingDryRunQuery}
                    jsonQuery={payload}
                    referencedAssets={referencedAssets ?? []}
                />
            </AnimatedWrapper>

            <SearchResultCard selectedAssetsOrFilters={previousFilters} />
        </div>
    );
};

const UpdateChartState: React.FC<{
    realtimeQuery: string;
    loadResult: LoadResponse | undefined;
    renderedQuery: string | undefined;
}> = ({ realtimeQuery, loadResult, renderedQuery }) => {
    const { updateChartState } = useChartWrapperContext();
    React.useEffect(() => {
        updateChartState({
            chartKey: "intelligence-search",
            jsonQuery: realtimeQuery ? JSON.parse(realtimeQuery) : undefined,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [realtimeQuery]);
    React.useEffect(() => {
        updateChartState({
            chartKey: "intelligence-search",
            renderedQuery: renderedQuery,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderedQuery]);

    React.useEffect(() => {
        updateChartState({
            chartKey: "intelligence-search",
            dataSource: loadResult?.data ?? [],
            modelAlias: "firebase",
            queryOption: {},
            meta: loadResult?.meta,
            elapsed: loadResult?.meta?.estimated_bytes,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadResult?.data, loadResult?.meta, loadResult?.query]);
    return null;
};
export const SearchComponentMemo = React.memo(SearchComponent, (prev, next) => {
    const { onChange: _1, form: _2, ...restPrev } = prev;
    const { onChange: _3, form: _4, ...restNext } = next;
    // console.log("SearchComponent ", restPrev.formListFieldData.name, { restPrev, restNext });
    return JSON.stringify(restPrev) === JSON.stringify(restNext);
});
